import React, { useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";
import { useDispatch, useSelector } from "react-redux";
import { CheckCircle2, Eye, FileText, RefreshCcw, Edit, RotateCw, Trash2, Plus, X } from "lucide-react";
import { 
    payrollFetchBulletinLinesRequest, 
    payrollFetchResourceRequest, 
    payrollSaveResourceRequest 
} from "../../../redux/actions/payrollActions";
import { postRequest, getRequest, putRequest, deleteRequest, API_BASE_URL } from "../../../../api";
import "./PaieCyclePages.css";

const userName = (user) => user?.fullName || `${user?.firstName || ""} ${user?.lastName || ""}`.trim() || user?.username || `#${user?.id}`;

const formatBulletinLineAmount = (line) => {
    const montant = Number(line?.montant || 0);
    const impact = line?.type === "RETENUE" ? -montant : montant;
    const signe = impact > 0 ? "+" : impact < 0 ? "−" : "";
    return `${signe}${Math.abs(montant).toLocaleString("fr-FR")}`;
};

const bulletinLineAmountClass = (line) => {
    const montant = Number(line?.montant || 0);
    const impact = line?.type === "RETENUE" ? -montant : montant;
    return impact < 0 ? "pcyc-amount-negative" : "pcyc-amount-positive";
};

const ValidationBulletinsPage = () => {
    const dispatch = useDispatch();
    const payroll = useSelector((state) => state.payroll || {});
    const [periodeId, setPeriodeId] = useState("");
    const [selected, setSelected] = useState(null);

    // Variable editing states
    const [editVarsOpen, setEditVarsOpen] = useState(false);
    const [variables, setVariables] = useState([]);
    const [editingVarId, setEditingVarId] = useState(null);
    const [varForm, setVarForm] = useState({ rubriqueId: "", typeHeureSupplementaireId: "", valeur: "", commentaire: "" });
    const [savingVar, setSavingVar] = useState(false);
    const [loadingVars, setLoadingVars] = useState(false);

    useEffect(() => {
        dispatch(payrollFetchResourceRequest("periodes"));
        dispatch(payrollFetchResourceRequest("bulletins"));
        dispatch(payrollFetchResourceRequest("rubriques"));
        dispatch(payrollFetchResourceRequest("typesHeuresSupplementaires"));
    }, [dispatch]);

    // Auto-select first period
    useEffect(() => {
        if (!periodeId && payroll.periodes?.length > 0) {
            setPeriodeId(String(payroll.periodes[0].id));
        }
    }, [payroll.periodes, periodeId]);

    const bulletins = useMemo(() => {
        if (!periodeId) return payroll.bulletins || [];
        return (payroll.bulletins || []).filter((b) => String(b.periodeId) === String(periodeId));
    }, [payroll.bulletins, periodeId]);

    const selectedRubrique = useMemo(
        () => (payroll.rubriques || []).find((rubrique) => String(rubrique.id) === String(varForm.rubriqueId)),
        [payroll.rubriques, varForm.rubriqueId]
    );
    const isHeureSupplementaire = Boolean(varForm.typeHeureSupplementaireId);
    const valeurLabel = isHeureSupplementaire
        ? "Nombre d'heures"
        : "Montant de la correction (MAD)";
    const valeurStep = isHeureSupplementaire ? "0.25" : "0.01";

    const previewCorrection = useMemo(() => {
        const valeur = Number(varForm.valeur);
        if (!Number.isFinite(valeur) || valeur === 0 || !selectedRubrique) return null;

        if (isHeureSupplementaire) {
            if (valeur < 0) return null;
            const typeHs = (payroll.typesHeuresSupplementaires || []).find(
                (type) => String(type.id) === String(varForm.typeHeureSupplementaireId)
            );
            const coefficient = Number(typeHs?.coefficient);
            const tauxApplique = Number.isFinite(coefficient) && coefficient > 1
                ? coefficient
                : 1 + (Number.isFinite(coefficient) ? coefficient : 0);
            const montant = valeur * Number(selected?.tauxHoraire || 0) * tauxApplique;
            return { montant, label: "Montant brut estimé des heures supplémentaires" };
        }

        return selectedRubrique.type === "RETENUE"
            ? { montant: valeur, label: "Impact estimé sur le net à payer" }
            : { montant: valeur, label: "Impact brut avant cotisations et IR" };
    }, [isHeureSupplementaire, payroll.typesHeuresSupplementaires, selected?.tauxHoraire, selectedRubrique, varForm.typeHeureSupplementaireId, varForm.valeur]);

    const variableLabel = (variable) => {
        if (variable.typeHeureSupplementaireId) return `${variable.valeur} heure(s)`;
        const rubrique = (payroll.rubriques || []).find((item) => String(item.id) === String(variable.rubriqueId));
        const valeur = Number(variable.valeur || 0);
        const signe = valeur > 0 ? "+" : valeur < 0 ? "−" : "";
        return `${signe}${Math.abs(valeur).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} MAD${rubrique?.type === "RETENUE" ? " (retenue)" : ""}`;
    };

    const open = (bulletin) => {
        setSelected(bulletin);
        dispatch(payrollFetchBulletinLinesRequest(bulletin.id));
    };

    const validate = (bulletin) => {
        dispatch(payrollSaveResourceRequest("bulletins", {
            userId: bulletin.userId,
            periodeId: bulletin.periodeId,
            salaireBase: bulletin.salaireBase,
            nombreJoursTravailles: bulletin.nombreJoursTravailles,
            tauxHoraire: bulletin.tauxHoraire,
            montantHeuresSupplementaires: bulletin.montantHeuresSupplementaires,
            salaireBrutImposable: bulletin.salaireBrutImposable,
            baseCnss: bulletin.baseCnss,
            totalCotisationsSalariales: bulletin.totalCotisationsSalariales,
            totalCotisationsPatronales: bulletin.totalCotisationsPatronales,
            fraisProfessionnels: bulletin.fraisProfessionnels,
            revenuNetImposable: bulletin.revenuNetImposable,
            ir: bulletin.ir,
            totalRetenues: bulletin.totalRetenues,
            salaireBrutGlobal: bulletin.salaireBrutGlobal,
            gainNet: bulletin.gainNet,
            arrondi: bulletin.arrondi,
            netAPayer: bulletin.netAPayer,
            modePaiement: bulletin.modePaiement,
            statut: "VALIDE",
            dateCalcul: bulletin.dateCalcul,
            calculeParId: bulletin.calculeParId
        }, bulletin.id));

        if (selected && selected.id === bulletin.id) {
            setSelected({ ...bulletin, statut: "VALIDE" });
        }
    };

    const validateAll = async () => {
        const toValidate = bulletins.filter(b => b.statut !== "VALIDE");
        if (toValidate.length === 0) {
            Swal.fire({ icon: "info", text: "Tous les bulletins de cette période sont déjà validés." });
            return;
        }

        const confirm = await Swal.fire({
            icon: "question",
            title: "Valider en masse ?",
            text: `${toValidate.length} bulletin(s) seront marqués VALIDE.`,
            showCancelButton: true,
            confirmButtonColor: "#204385",
            confirmButtonText: "Valider"
        });

        if (!confirm.isConfirmed) return;

        toValidate.forEach((bulletin) => validate(bulletin));
        Swal.fire({ icon: "success", title: "Validation lancée", text: "Les bulletins sont en cours de validation.", timer: 1500, showConfirmButton: false });
    };

    // Load variables for selected employee and period
    const loadVariables = async () => {
        if (!selected) return;
        setLoadingVars(true);
        try {
            const token = sessionStorage.getItem("token") || localStorage.getItem("token");
            const response = await getRequest({
                url: `${API_BASE_URL}/api/elements-variables-paie?periodeId=${selected.periodeId}&userId=${selected.userId}`,
                token
            });
            setVariables(response.data || []);
        } catch (err) {
            console.error("Error loading variables:", err);
        }
        setLoadingVars(false);
    };

    const handleOpenVars = () => {
        if (selected?.statut === "VALIDE") {
            Swal.fire({ icon: "info", text: "Ce bulletin est validé : les variables ne sont plus modifiables." });
            return;
        }
        setEditVarsOpen(true);
        loadVariables();
    };

    const handleSaveVar = async () => {
        if (!varForm.rubriqueId || varForm.valeur === "" || !varForm.commentaire.trim()) {
            Swal.fire({ icon: "warning", text: "Rubrique, montant (ou nombre d'heures) et motif sont obligatoires." });
            return;
        }
        if (Number(varForm.valeur) === 0 || (isHeureSupplementaire && Number(varForm.valeur) < 0)) {
            Swal.fire({ icon: "warning", text: isHeureSupplementaire ? "Le nombre d'heures doit être supérieur à 0." : "Le montant de la correction ne peut pas être nul." });
            return;
        }

        setSavingVar(true);
        const token = sessionStorage.getItem("token") || localStorage.getItem("token");
        const payload = {
            userId: selected.userId,
            periodeId: selected.periodeId,
            rubriqueId: Number(varForm.rubriqueId),
            typeHeureSupplementaireId: varForm.typeHeureSupplementaireId ? Number(varForm.typeHeureSupplementaireId) : null,
            valeur: Number(varForm.valeur),
            commentaire: varForm.commentaire || null
        };

        try {
            if (editingVarId) {
                await putRequest({
                    url: `${API_BASE_URL}/api/elements-variables-paie/${editingVarId}`,
                    data: payload,
                    token
                });
            } else {
                await postRequest({
                    url: `${API_BASE_URL}/api/elements-variables-paie`,
                    data: payload,
                    token
                });
            }
            setVarForm({ rubriqueId: "", typeHeureSupplementaireId: "", valeur: "", commentaire: "" });
            setEditingVarId(null);
            loadVariables();
        } catch (err) {
            Swal.fire({ icon: "error", title: "Erreur", text: "Impossible d'enregistrer la variable." });
        }
        setSavingVar(false);
    };

    const handleEditVar = (v) => {
        setEditingVarId(v.id);
        setVarForm({
            rubriqueId: v.rubriqueId || "",
            typeHeureSupplementaireId: v.typeHeureSupplementaireId || "",
            valeur: v.valeur || "",
            commentaire: v.commentaire || ""
        });
    };

    const handleDeleteVar = async (id) => {
        const confirm = await Swal.fire({
            icon: "warning",
            title: "Supprimer la variable ?",
            showCancelButton: true,
            confirmButtonColor: "#334785"
        });
        if (!confirm.isConfirmed) return;

        const token = sessionStorage.getItem("token") || localStorage.getItem("token");
        try {
            await deleteRequest({
                url: `${API_BASE_URL}/api/elements-variables-paie/${id}`,
                token
            });
            loadVariables();
        } catch (err) {
            Swal.fire({ icon: "error", title: "Erreur", text: "Impossible de supprimer." });
        }
    };

    const handleRecalculate = async () => {
        if (!selected) return;
        Swal.fire({
            title: "Recalcul en cours...",
            allowOutsideClick: false,
            didOpen: () => {
                Swal.showLoading();
            }
        });

        const token = sessionStorage.getItem("token") || localStorage.getItem("token");
        try {
            const response = await postRequest({
                url: `${API_BASE_URL}/api/bulletins-paie/calculate`,
                data: {
                    userId: selected.userId,
                    periodeId: selected.periodeId,
                    nombreJoursTravailles: selected.nombreJoursTravailles || 26,
                    modePaiement: selected.modePaiement || "VIREMENT"
                },
                token
            });

            // Update local state and re-fetch lines
            setSelected(response.data);
            dispatch(payrollFetchBulletinLinesRequest(response.data.id));
            dispatch(payrollFetchResourceRequest("bulletins"));

            Swal.fire({
                icon: "success",
                title: "Calcul mis à jour",
                timer: 1500,
                showConfirmButton: false
            });
            setEditVarsOpen(false);
        } catch (error) {
            Swal.fire({
                icon: "error",
                title: "Échec du calcul",
                text: error?.response?.data?.message || "Erreur lors du recalcul."
            });
        }
    };

    return (
        <div className="pcyc-page">
            <div className="pcyc-header">
                <div>
                    <h2><FileText size={18} />Validation des bulletins</h2>
                    <p>Consultation des bulletins calculés, détail ligne par ligne, correction de variables et validation.</p>
                </div>
                <button className="pcyc-btn pcyc-btn-blue" type="button" onClick={() => dispatch(payrollFetchResourceRequest("bulletins"))}>
                    <RefreshCcw size={15} /> Actualiser
                </button>
            </div>
            
            <div className="pcyc-toolbar">
                <label>
                    <span>Période</span>
                    <select value={periodeId} onChange={(e) => setPeriodeId(e.target.value)}>
                        <option value="">Sélectionner</option>
                        {(payroll.periodes || []).map((p) => (
                            <option key={p.id} value={p.id} disabled={p.statut === "CLOTUREE"}>
                                {String(p.mois).padStart(2, "0")}/{p.annee} - {p.statut} {p.statut === "CLOTUREE" ? "(Déjà clôturée)" : ""}
                            </option>
                        ))}
                    </select>
                </label>
                <button className="pcyc-btn pcyc-btn-primary pcyc-validation-all" type="button" onClick={validateAll}>
                    <CheckCircle2 size={15} /> Validation en masse
                </button>
                <div className="pcyc-meta">{bulletins.length} bulletin(s)</div>
            </div>

            <div className="pcyc-table-card pcyc-table-card--full">
                    <table className="pcyc-table">
                        <thead>
                            <tr>
                                <th>Collaborateur</th>
                                <th>Période</th>
                                <th>Brut Imposable</th>
                                <th>Net à payer</th>
                                <th>Statut</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {bulletins.length === 0 ? (
                                <tr><td colSpan="6" className="pcyc-empty">Aucun bulletin calculé pour cette période</td></tr>
                            ) : (
                                bulletins.map((b) => (
                                    <tr key={b.id} style={{ background: selected?.id === b.id ? "#f1f5f9" : "transparent" }}>
                                        <td><strong>{b.userFullName || b.userId}</strong></td>
                                        <td><span className="pcyc-code">{b.mois ? `${String(b.mois).padStart(2, "0")}/${b.annee}` : b.periodeId}</span></td>
                                        <td>{Number(b.salaireBrutImposable || 0).toLocaleString("fr-FR")} MAD</td>
                                        <td><strong>{Number(b.netAPayer || 0).toLocaleString("fr-FR")} MAD</strong></td>
                                        <td><span className={`pcyc-status ${b.statut}`}>{b.statut}</span></td>
                                        <td className="pcyc-row-actions">
                                            <button type="button" onClick={() => open(b)} title="Visualiser le détail"><Eye size={14} /></button>
                                            <button type="button" disabled={b.statut === "VALIDE"} onClick={() => validate(b)} title="Valider le bulletin" style={b.statut === "VALIDE" ? { color: "#2e7d32" } : undefined}><CheckCircle2 size={14} /></button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
            </div>

            {selected && (
                <div className="pcyc-overlay" onClick={() => setSelected(null)}>
                    <div className="pcyc-modal pcyc-modal--detail" onClick={(e) => e.stopPropagation()}>
                        <div className="pcyc-modal-header">
                            <h3>Détail du bulletin — {selected.userFullName}</h3>
                            <button type="button" onClick={() => setSelected(null)} aria-label="Fermer"><X size={18} /></button>
                        </div>
                        <div className="pcyc-modal-body">
                            <div className="pcyc-summary pcyc-detail-summary">
                                <strong>{selected.userFullName}</strong>
                                <div>
                                    <span>Jours travaillés : {selected.nombreJoursTravailles}</span>
                                    <span>Paiement : {selected.modePaiement}</span>
                                </div>
                                <b>Net à payer : {Number(selected.netAPayer || 0).toLocaleString("fr-FR")} MAD</b>
                            </div>
                            <div className="pcyc-detail-lines">
                                <table className="pcyc-table pcyc-small-table">
                                    <thead>
                                        <tr><th>Rubrique / Libellé</th><th>Base</th><th>Taux</th><th>Montant</th></tr>
                                    </thead>
                                    <tbody>
                                        {(payroll.bulletinLines || []).map((line) => (
                                            <tr key={line.id}>
                                                <td>{line.rubriqueLibelle || line.libelle}</td>
                                                <td>{line.baseCalcul != null ? Number(line.baseCalcul).toLocaleString("fr-FR") : "—"}</td>
                                                <td>{line.tauxApplique != null ? `${(Number(line.tauxApplique) * 100).toFixed(2)}%` : "—"}</td>
                                                <td className={bulletinLineAmountClass(line)}>
                                                    {formatBulletinLineAmount(line)}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            <div className="pcyc-actions pcyc-detail-actions">
                                <button className="pcyc-btn pcyc-btn-light" type="button" disabled={selected.statut === "VALIDE"} onClick={handleOpenVars}>
                                    <Edit size={14} /> Corriger variables
                                </button>
                                <button className="pcyc-btn pcyc-btn-primary" type="button" disabled={selected.statut === "VALIDE"} onClick={() => validate(selected)} style={selected.statut === "VALIDE" ? { background: "#2e7d32", color: "#fff" } : undefined}>
                                    <CheckCircle2 size={14} /> Valider
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Variables Corrections Modal */}
            {editVarsOpen && selected && (
                <div className="pcyc-overlay" onClick={() => setEditVarsOpen(false)}>
                    <div className="pcyc-modal" style={{ width: "850px" }} onClick={(e) => e.stopPropagation()}>
                        <div className="pcyc-modal-header">
                            <h3>Variables de paie - {selected.userFullName} ({String(selected.mois).padStart(2, "0")}/{selected.annee})</h3>
                            <button type="button" onClick={() => setEditVarsOpen(false)}><X size={18} /></button>
                        </div>
                        <div className="pcyc-modal-body">
                            <section className="pcyc-correction-preview">
                                <div className="pcyc-correction-title">
                                    <FileText size={14} /> Lignes calculées du bulletin
                                    <span>Lecture seule — elles sont mises à jour après recalcul.</span>
                                </div>
                                {(payroll.bulletinLines || []).length === 0 ? (
                                    <p className="pcyc-help">Aucune ligne calculée à afficher.</p>
                                ) : (
                                    <div className="pcyc-correction-table-wrap">
                                        <table className="pcyc-table pcyc-small-table">
                                            <thead>
                                                <tr>
                                                    <th>Rubrique</th>
                                                    <th>Base</th>
                                                    <th>Taux</th>
                                                    <th>Montant</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {(payroll.bulletinLines || []).map((line) => (
                                                    <tr key={line.id}>
                                                        <td>{line.rubriqueLibelle || line.libelle}</td>
                                                        <td>{line.baseCalcul != null ? Number(line.baseCalcul).toLocaleString("fr-FR") : "—"}</td>
                                                        <td>{line.tauxApplique != null ? `${(Number(line.tauxApplique) * 100).toFixed(2)}%` : "—"}</td>
                                                        <td className={bulletinLineAmountClass(line)}>
                                                            {formatBulletinLineAmount(line)}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </section>

                            <div className="pcyc-correction-grid">
                                
                                {/* Current variables list */}
                                <div>
                                    <h4 style={{ color: "#204385", fontSize: "13px", marginBottom: "8px" }}>Variables ajoutées</h4>
                                    {loadingVars ? (
                                        <p className="pcyc-help">Chargement des variables...</p>
                                    ) : variables.length === 0 ? (
                                        <p className="pcyc-help">Aucune variable ajoutée. Utilisez le formulaire ci-contre pour créer une correction.</p>
                                    ) : (
                                        <table className="pcyc-table" style={{ minWidth: "100%" }}>
                                            <thead>
                                                <tr>
                                                    <th>Rubrique</th>
                                                        <th>Correction</th>
                                                        <th>Motif</th>
                                                    <th>Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {variables.map((v) => (
                                                    <tr key={v.id}>
                                                        <td><strong>{v.rubriqueLibelle}</strong></td>
                                                        <td>{variableLabel(v)}</td>
                                                        <td>{v.commentaire || "-"}</td>
                                                        <td className="pcyc-row-actions">
                                                            <button type="button" onClick={() => handleEditVar(v)}><Edit size={13} /></button>
                                                            <button type="button" onClick={() => handleDeleteVar(v.id)}><Trash2 size={13} style={{ color: "#334785" }} /></button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    )}
                                </div>

                                {/* Quick Saisie / Form */}
                                <div className="pcyc-correction-form">
                                    <h4 style={{ color: "#204385", fontSize: "13px", marginBottom: "8px" }}>
                                        {editingVarId ? "Modifier la correction" : "Ajouter une correction"}
                                    </h4>
                                    <p className="pcyc-help">Choisissez une rubrique de paie puis saisissez un montant : + pour ajouter, − pour déduire, puis recalculez le bulletin.</p>
                                    <div className="pcyc-form-grid" style={{ gridTemplateColumns: "1fr", gap: "10px" }}>
                                        <label>
                                            <span>Rubrique</span>
                                            <select 
                                                value={varForm.rubriqueId} 
                                                onChange={(e) => {
                                                    const rubrique = (payroll.rubriques || []).find((item) => String(item.id) === e.target.value);
                                                    setVarForm({ ...varForm, rubriqueId: e.target.value, typeHeureSupplementaireId: rubrique?.type === "RETENUE" ? "" : varForm.typeHeureSupplementaireId });
                                                }}
                                            >
                                                <option value="">Sélectionner</option>
                                                {(payroll.rubriques || []).filter(r => r.active !== false).map(r => (
                                                    <option key={r.id} value={r.id}>{r.code} - {r.libelle}</option>
                                                ))}
                                            </select>
                                        </label>
                                        {selectedRubrique?.type !== "RETENUE" && (
                                            <label>
                                                <span>Type d'heure supplémentaire <small>(optionnel)</small></span>
                                                <select 
                                                    value={varForm.typeHeureSupplementaireId} 
                                                    onChange={(e) => setVarForm({ ...varForm, typeHeureSupplementaireId: e.target.value })}
                                                >
                                                    <option value="">Aucune — prime / indemnité</option>
                                                    {(payroll.typesHeuresSupplementaires || []).map(h => (
                                                        <option key={h.id} value={h.id}>{h.code} - {h.libelle}</option>
                                                    ))}
                                                </select>
                                            </label>
                                        )}
                                        <label>
                                            <span>{valeurLabel}</span>
                                            <input 
                                                type="number" 
                                                min={isHeureSupplementaire ? "0.25" : undefined}
                                                step={valeurStep}
                                                value={varForm.valeur} 
                                                onChange={(e) => setVarForm({ ...varForm, valeur: e.target.value })} 
                                            />
                                        </label>
                                        <label>
                                            <span>Motif <small>(obligatoire)</small></span>
                                            <input 
                                                value={varForm.commentaire} 
                                                onChange={(e) => setVarForm({ ...varForm, commentaire: e.target.value })} 
                                                placeholder="Ex. régularisation sur la période précédente"
                                            />
                                        </label>
                                    </div>
                                    {previewCorrection && (
                                        <div className={`pcyc-correction-impact ${previewCorrection.montant < 0 ? "is-negative" : ""}`}>
                                            <span>{previewCorrection.label}</span>
                                            <strong>{previewCorrection.montant < 0 ? "−" : "+"}{Math.abs(previewCorrection.montant).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} MAD</strong>
                                            {selectedRubrique?.type !== "RETENUE" && <small>Le net exact sera calculé après prise en compte des cotisations et de l'IR.</small>}
                                        </div>
                                    )}
                                    <div className="pcyc-actions" style={{ marginTop: "14px" }}>
                                        {editingVarId && (
                                            <button 
                                                className="pcyc-btn pcyc-btn-light" 
                                                type="button" 
                                                onClick={() => { setEditingVarId(null); setVarForm({ rubriqueId: "", typeHeureSupplementaireId: "", valeur: "", commentaire: "" }); }}
                                            >
                                                Annuler
                                            </button>
                                        )}
                                        <button 
                                            className="pcyc-btn pcyc-btn-primary" 
                                            type="button" 
                                            disabled={savingVar} 
                                            onClick={handleSaveVar}
                                        >
                                            Enregistrer
                                        </button>
                                    </div>
                                </div>
                            </div>
                            <div className="pcyc-actions" style={{ borderTop: "1px solid #e5e7eb", paddingTop: "14px", marginTop: "14px" }}>
                                <button className="pcyc-btn pcyc-btn-blue" type="button" onClick={handleRecalculate}>
                                    <RotateCw size={14} /> Recalculer le bulletin
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ValidationBulletinsPage;
